import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { BookUser, Loader2, Globe, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Directory() {
  const { selected } = useDomains();
  const did = selected?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!did) return; setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/directory`); setData(data); }
    catch (e) { toast.error(apiError(e)); setData(null); } finally { setLoading(false); }
  }, [did]);
  useEffect(() => { load(); }, [load]);

  if (!selected) return <NoDomain />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Directory & Roles" subtitle={selected.domain} icon={BookUser} />
      {loading ? <div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div> : data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="glass rounded-3xl p-5">
            <h3 className="font-heading mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800"><Globe className="h-5 w-5 text-emerald-600" />Domains & Aliases</h3>
            <div className="space-y-2">
              {data.domains.map((d) => (
                <div key={d.domainName} className="flex items-center justify-between rounded-2xl bg-white/60 p-3" data-testid={`gws-domain-${d.domainName}`}>
                  <span className="font-medium text-slate-800">{d.domainName}</span>
                  <div className="flex gap-1">{d.isPrimary && <Badge className="bg-emerald-100 text-emerald-700">Primary</Badge>}{d.verified && <Badge className="bg-teal-50 text-teal-700">Verified</Badge>}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="glass rounded-3xl p-5">
            <h3 className="font-heading mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800"><ShieldCheck className="h-5 w-5 text-emerald-600" />Admin Roles</h3>
            <div className="space-y-2">
              {data.roles.map((r) => (
                <div key={r.roleId} className="flex items-center justify-between rounded-2xl bg-white/60 p-3" data-testid={`role-${r.roleId}`}>
                  <span className="font-medium text-slate-800">{r.roleName}</span>
                  <div className="flex gap-1">{r.isSuperAdminRole && <Badge className="bg-amber-100 text-amber-700">Super Admin</Badge>}{r.isSystemRole && <Badge className="bg-slate-100 text-slate-600">System</Badge>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
